<script>
  import { onMount, onDestroy } from 'svelte';
  import WelcomeVideoAjax from '@ajax/welcome_video';
  import ComponentEvents from '@utils/component-events';
  import Track from '@utils/track';
  import { getCurrentUserStore } from '@stores/current_user';

  export let leftPosition = '40px'; // Customizable left edge position
  export let bottomPosition = '40px';
  export let mode = 'modal';

  let showThumbnail = true;
  let showVideoModal = false;
  let videoElement;
  let videoEnded = false;
  let mounted = false;
  let hasBeenOpened = false;
  let isClosing = false;

  let showWelcomeVideoModal = false;
  let showWelcomeVideoNotification = false;
  let progressCheckInterval;

  let currentUser = getCurrentUserStore('default');

  const videoUrl = 'https://benable.s3.amazonaws.com/static/videos/welcome-video.mp4';
  const thumbnailUrl = 'https://benable.s3.amazonaws.com/static/videos/welcome-thumbnail.png';

  const videoDuration = 120; // 2 minutes in seconds
  const endThreshold = 10; // Consider video "ended" when 10 seconds from end

  onMount(() => {
    loadWelcomeVideoData();

    mounted = true;
  });

  onDestroy(() => {
    mounted = false;

    if (progressCheckInterval) {
      clearInterval(progressCheckInterval);
    }
  });

  async function loadWelcomeVideoData() {
    const data = await WelcomeVideoAjax.index();

    if (data) {
      showWelcomeVideoModal = data.show_welcome_video_modal;
      showWelcomeVideoNotification = data.show_welcome_video_notification;
    }
  }

  function disableWelcomeVideo() {
    WelcomeVideoAjax.disable();
  }

  function handleThumbnailClick() {
    showThumbnail = false;
    showVideoModal = true;
    videoEnded = false;
    hasBeenOpened = true;

    // Track video play event
    Track.welcomeVideoPlay({ currentUser, mode: 'thumbnail' });

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Start playing video when modal opens
    setTimeout(() => {
      if (videoElement && mounted) {
        videoElement.play();
        startProgressCheck();
      }
    }, 100);
  }

  function handleModalClose() {
    // Track video close event
    const timestamp = videoElement ? videoElement.currentTime : 0;
    const finished = videoEnded;
    Track.welcomeVideoClose({ currentUser, finished, timestamp });

    showVideoModal = false;

    // Clear progress check interval
    if (progressCheckInterval) {
      clearInterval(progressCheckInterval);
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // If video hasn't ended, show thumbnail again
    if (!videoEnded && mounted) {
      showThumbnail = true;
    }

    // Pause video when modal closes
    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
  }

  function handleVideoEnded() {
    videoEnded = true;
    disableWelcomeVideo();

    if (progressCheckInterval) {
      clearInterval(progressCheckInterval);
    }
  }

  function startProgressCheck() {
    if (progressCheckInterval) {
      clearInterval(progressCheckInterval);
    }

    progressCheckInterval = setInterval(() => {
      if (videoElement && !videoEnded && mounted) {
        const currentTime = videoElement.currentTime;
        const remainingTime = videoDuration - currentTime;

        // Check if we're within 5 seconds of the end
        if (remainingTime <= endThreshold) {
          handleVideoEnded();
        }
      }
    }, 1000); // Check every second
  }

  function handleVideoClick(e) {
    // Toggle play/pause on video click (excluding controls area)
    const clickY = e.offsetY;
    const height = e.target.offsetHeight;

    // Avoid interference with controls (bottom 18% of video)
    if (clickY > 0.82 * height) return;

    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  }

  function handleOverlayClick(e) {
    // Close modal when clicking on overlay (not on video)
    if (e.target === e.currentTarget) {
      handleModalClose();
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      handleModalClose();
    }
  }

  function showClosingAnimation(event) {
    isClosing = true;

    // Track video minimize event
    const timestamp = videoElement ? videoElement.currentTime : 0;
    Track.welcomeVideoDismissThumbnail({ currentUser, timestamp });

    // Get notification bell position
    const notificationBell = document.getElementById('notification-bell-button');

    if (notificationBell) {
      const bellRect = notificationBell.getBoundingClientRect();
      const thumbnailElement = event.target.closest('.video-thumbnail');
      const thumbnailRect = thumbnailElement.getBoundingClientRect();

      // Calculate the distance to move
      const deltaX = bellRect.left + bellRect.width / 2 - (thumbnailRect.left + thumbnailRect.width / 2);
      const deltaY = bellRect.top + bellRect.height / 2 - (thumbnailRect.top + thumbnailRect.height / 2);

      // Apply the animation transform
      thumbnailElement.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0.1)`;
      thumbnailElement.style.opacity = '0';

      setTimeout(() => {
        isClosing = false;
        showThumbnail = false;
      }, 600);
    }
  }

  function handleNotificationClick() {
    // Track video play event from notification
    Track.welcomeVideoPlay({ currentUser, mode: 'notification' });

    showVideoModal = true;
    showWelcomeVideoModal = false;
    showWelcomeVideoNotification = false;

    disableWelcomeVideo();
    document.body.style.overflow = 'hidden';

    const thumbnailElement = document.querySelector('.welcome-video-thumbnail');

    if (thumbnailElement) {
      thumbnailElement.style.display = 'none';
    }

    // Start progress check when playing from notification
    setTimeout(() => {
      if (videoElement && mounted) {
        startProgressCheck();
      }
    }, 100);
  }

  function handleThumbnailClose(e) {
    e.stopPropagation();
    showClosingAnimation(e);
    disableWelcomeVideo();

    ComponentEvents.trigger('update-count', 1);
  }
</script>

<style lang="scss">
  .video-thumbnail {
    position: fixed;
    width: 138px;
    height: 213.273px;
    border-radius: 17.25px;
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%),
      var(--thumbnail-bg) lightgray 50% / cover no-repeat;
    box-shadow: 0px 5.75px 86.25px 0px rgba(0, 0, 0, 0.25);
    flex-shrink: 0;
    aspect-ratio: 138/213.27;
    cursor: pointer;
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 50;

    &:hover {
      transform: scale(1.05);
    }

    &.closing {
      pointer-events: none;
    }
  }

  .play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    &::after {
      content: '';
      width: 0;
      height: 0;
      border-left: 16px solid #333;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      margin-left: 4px;
    }
  }

  .thumbnail-close-button {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    color: white;
    transition: background 0.2s ease;
    z-index: 10;

    &:hover {
      background: rgba(0, 0, 0, 0.9);
    }

    &::before {
      content: '✕';
    }
  }

  .video-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(22, 13, 54, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .video-modal-container {
    position: relative;
    max-width: 370px;
    max-height: 570px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: black;
    border-radius: 8px;
  }

  .video-close-button {
    position: absolute;
    top: -50px;
    right: 0;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 24px;
    color: #333;
    transition: background 0.2s ease;
    z-index: 10;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    &::before {
      content: '✕';
    }
  }

  .video-player {
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: 570px;
    cursor: pointer;
    border-radius: 8px;
  }

  @media (max-height: 644px) {
    .video-player {
      max-height: 450px;
    }
  }

  @media (max-width: 768px) {
    .video-close-button {
      top: -40px;
      right: 10px;
      background: transparent;
      color: white;
      // backdrop-filter: blur(10px);

      &:hover {
        background: transparent;
      }
    }

    .video-modal-overlay {
      padding: 10px;
      padding-bottom: 40px;
    }
  }

  .video-notification {
    display: flex;
    padding: 8px 12px;
    gap: 12px;
    margin: 16px;
    border-radius: 16px;
    background: radial-gradient(437.26% 216.24% at 6.12% 132.54%, #cba0f6 0%, #4daeff 100%);
    cursor: pointer;

    .video-notification-img {
      border-radius: 8px;
      width: 36px;
      height: 43px;
      flex-shrink: 0;
    }

    .video-notification-arrow {
      display: flex;
      align-items: center;
    }

    .video-notification-text-container {
      display: flex;
      flex-direction: column;
      text-align: left;
      flex-grow: 1;

      .video-notification-text-1 {
        color: #fff;
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
      }

      .video-notification-text-2 {
        color: #fff;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
      }
    }
  }
</style>

{#if showVideoModal}
  <div
    class="video-modal-overlay"
    on:click={handleOverlayClick}
    on:keydown={handleKeydown}
    role="dialog"
    aria-modal="true"
    tabindex="-1">
    <div class="video-modal-container">
      <button class="video-close-button" on:click={handleModalClose} aria-label="Close video"> </button>
      <video
        bind:this={videoElement}
        class="video-player"
        controls
        loop
        autoplay
        on:click|preventDefault|stopPropagation={handleVideoClick}
        controlsList="nodownload"
        disablePictureInPicture={true}>
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  </div>
{/if}

{#if mode === 'modal'}
  {#if showThumbnail && mounted && showWelcomeVideoModal}
    <div
      class="video-thumbnail welcome-video-thumbnail"
      class:closing={isClosing}
      style="left: {leftPosition}; bottom: {bottomPosition}; --thumbnail-bg: url('{thumbnailUrl}')"
      on:click={handleThumbnailClick}
      role="button"
      tabindex="0"
      on:keydown={(e) => e.key === 'Enter' && handleThumbnailClick()}>
      <div class="play-button"></div>
      {#if hasBeenOpened}
        <button class="thumbnail-close-button" on:click={handleThumbnailClose} aria-label="Remove video"> </button>
      {/if}
    </div>
  {/if}
{/if}

{#if mode === 'notification' && showWelcomeVideoNotification}
  <div class="video-notification" on:click|preventDefault={handleNotificationClick}>
    <img
      class="video-notification-img"
      src="/images/welcome-modal/welcome-modal-notif.png"
      srcset="/images/welcome-modal/welcome-modal-notif.png 1x, /images/welcome-modal/welcome-modal-notif@2x.png 2x" />
    <div class="video-notification-text-container">
      <div class="video-notification-text-1">Welcome to Benable!</div>
      <div class="video-notification-text-2">Overview video</div>
    </div>
    <div class="video-notification-arrow">
      <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M11.1445 7.26587C11.8231 7.94837 11.8229 9.05103 11.1445 9.73364L5.40918 15.5022L4.87695 14.9729L4.3457 14.4436L10.0801 8.67603C10.177 8.57851 10.177 8.421 10.0801 8.32349L4.3457 2.55591L4.87695 2.02759L5.40918 1.49829L11.1445 7.26587Z"
          fill="white" />
      </svg>
    </div>
  </div>
{/if}
